import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { API_BASE_URL } from '../api';
import Template1 from '../components/Template1/Template1';
import Template2 from '../components/Template2/Template2';
import Template3 from '../components/Template3/Template3';
import Template4 from '../components/Template4/Template4';
import Template5 from '../components/Template5/Template5';
import Template6 from '../components/Template6/Template6';
import Template7 from '../components/Template7/Template7';
import Template8 from '../components/Template8/Template8';
import Template9 from '../components/Template9/Template9';
import { LocalBusinessSchema } from '../components/SchemaScript';
import { generateCompleteSEO } from '../utils/seo';
import { stripEmailFromTemplateData } from '../utils/templateContactSanitizer';
import SEOHead from '../components/SEOHead';

function renderTemplate(id, real) {
  const n = Number(id);
  if (n === 1) return <Template1 templateData={real} />;
  if (n === 2) return <Template2 templateData={real} />;
  if (n === 3) return <Template3 templateData={real} />;
  if (n === 4) return <Template4 templateData={real} />;
  if (n === 5) return <Template5 templateData={real} />;
  if (n === 6) return <Template6 templateData={real} />;
  if (n === 7) return <Template7 templateData={real} />;
  if (n === 8) return <Template8 templateData={real} />;
  if (n === 9) return <Template9 templateData={real} />;
  return <Template1 templateData={real} />;
}

function normalizeTemplateId(value) {
  const n = Number(value);
  return Number.isFinite(n) && n > 0 ? n : null;
}

function inferTemplateIdFromMeta(meta) {
  if (!meta || typeof meta !== 'object') return null;

  const direct =
    normalizeTemplateId(meta.id) ||
    normalizeTemplateId(meta.templateId) ||
    normalizeTemplateId(meta.template_id);
  if (direct) return direct;

  const image = String(meta.image || '').toLowerCase();
  const imageMatch = image.match(/template(\d+)/);
  if (imageMatch) {
    const fromImage = normalizeTemplateId(imageMatch[1]);
    if (fromImage) return fromImage;
  }

  const name = String(meta.name || '').toLowerCase();
  if (name.includes('nebula')) return 9;
  if (name.includes('premium dark')) return 8;
  if (name.includes('modern saas')) return 3;
  if (name.includes('portfolio minimal')) return 2;
  if (name.includes('e-commerce')) return 1;
  if (name.includes('agency bold')) return 5;

  return null;
}

function resolveTemplateId(listing, templateData) {
  return (
    normalizeTemplateId(listing?.template_id) ||
    normalizeTemplateId(templateData?.template_id) ||
    normalizeTemplateId(templateData?.templateId) ||
    normalizeTemplateId(templateData?.template?.id) ||
    normalizeTemplateId(templateData?.template?.templateId) ||
    inferTemplateIdFromMeta(templateData?.template) ||
    inferTemplateIdFromMeta(templateData)
  );
}

function parseTemplateData(rawTemplateData) {
  if (!rawTemplateData) return null;

  let parsed = rawTemplateData;
  for (let i = 0; i < 2; i += 1) {
    if (typeof parsed !== 'string') break;
    try {
      parsed = JSON.parse(parsed);
    } catch (e) {
      return null;
    }
  }

  return parsed && typeof parsed === 'object' ? parsed : null;
}

/**
 * Merge live GMB data with existing template data
 * Live data takes priority for dynamic fields (reviews, photos, posts)
 */
function mergeWithLiveData(templateData, liveData) {
  if (!liveData || !liveData.data) return templateData;

  const live = liveData.data;
  const merged = { ...templateData };

  // Merge testimonials/reviews - live data takes priority if available
  if (live.testimonials && live.testimonials.length > 0) {
    merged.testimonials = live.testimonials;
  }

  // Merge photos - live data takes priority if available
  if (live.photos && live.photos.length > 0) {
    merged.photos = live.photos;
    // Also update latestUpdates with fresh photos
    merged.latestUpdates = live.photos.slice(0, 8).map((p, i) => ({
      id: i + 1,
      image: p.url,
      date: 'Recent',
      title: p.name || `${merged.businessName || 'Business'} Update`,
      description: p.description || `Latest update`,
      isRealPhoto: true
    }));
  }

  // Merge GMB feed/posts - live data takes priority if available
  if (live.gmbFeed && live.gmbFeed.showSection) {
    merged.gmbFeed = live.gmbFeed;
  }

  // Merge business hours - live data takes priority if has open hours
  if (live.businessHours && live.businessHours.some(h => !h.isClosed)) {
    merged.businessHours = live.businessHours;
  }

  // Update rating info
  if (live.rating) {
    merged.businessInfo = {
      ...merged.businessInfo,
      rating: live.rating,
      reviewCount: live.reviewCount
    };
  }

  // Store last update timestamp
  merged._liveDataUpdatedAt = live.lastUpdated;

  return merged;
}

export default function BusinessView() {
  const { slug } = useParams();
  const [listing, setListing] = useState(null);
  const [liveData, setLiveData] = useState(null);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);

  // Fetch listing data
  useEffect(() => {
    (async () => {
      try {
        setLoading(true);
        const res = await fetch(`${API_BASE_URL}/api/v1/directory/listing/${encodeURIComponent(slug)}`);
        if (!res.ok) throw new Error(`Listing not found (${res.status})`);
        const j = await res.json();
        setListing(j);
      } catch (e) {
        setError(e.message);
      } finally {
        setLoading(false);
      }
    })();
  }, [slug]);

  // Fetch live GMB data (reviews, photos, posts) - runs after listing loads
  useEffect(() => {
    if (!listing || !slug) return;

    // Only fetch live data if listing has template data (is a free website)
    if (!listing.template_data) return;

    (async () => {
      try {
        const res = await fetch(`${API_BASE_URL}/api/v1/directory/listing/${encodeURIComponent(slug)}/live-data`);
        if (res.ok) {
          const data = await res.json();
          if (data.success) {
            setLiveData(data);
            console.log(`[BusinessView] Loaded live GMB data (cached: ${data.cached}, source: ${data.source})`);
          }
        }
      } catch (e) {
        // Silently fail - template data will still work
        console.warn('[BusinessView] Failed to fetch live GMB data:', e.message);
      }
    })();
  }, [listing, slug]);

  if (loading) return <div style={{ padding: 24 }}>Loading...</div>;
  if (error) return <div style={{ padding: 24, color: '#b00020' }}>Error: {error}</div>;
  if (!listing) return <div style={{ padding: 24 }}>Not found</div>;

  // If listing has template_data, render the template directly.
  // template_id can be missing for some listings, so resolve from template_data.
  if (listing.template_data) {
    try {
      const templateData = parseTemplateData(listing.template_data);
      if (!templateData) throw new Error('Invalid template data format');
      const resolvedTemplateId = resolveTemplateId(listing, templateData);
      let real = templateData?.template?.realData || templateData?.templateData || templateData?.realData || {};

      // Merge with live GMB data if available
      if (liveData && liveData.success) {
        real = mergeWithLiveData(real, liveData);
      }
      real = stripEmailFromTemplateData(real);

      if (real && Object.keys(real).length > 0) {
        // Build schema data from template data
        const schemaData = {
          name: real.businessName || real.name || listing.name,
          slug: slug,
          description: real.about || real.description || '',
          phone: real.phone || real.phoneNumber || '',
          email: '',
          streetAddress: real.address || '',
          city: real.city || listing.city || '',
          state: real.state || listing.state || '',
          postalCode: real.postalCode || real.zipCode || '',
          country: real.country || 'IN',
          latitude: real.latitude || listing.latitude,
          longitude: real.longitude || listing.longitude,
          logo: real.logo || real.businessLogo || '',
          images: real.photos || real.images || [],
          rating: real.businessInfo?.rating || real.rating || listing.rating,
          reviewCount: real.businessInfo?.reviewCount || real.reviewCount || listing.review_count,
          reviews: real.reviews || real.testimonials || [],
          services: real.services || [],
          faqs: real.faqs || [],
          openingHours: real.openingHours || real.businessHours || [],
          socialProfiles: [
            real.facebook,
            real.instagram,
            real.twitter,
            real.linkedin,
            real.youtube
          ].filter(Boolean),
          businessType: real.businessType || 'LocalBusiness'
        };

        const businessName = schemaData.name;
        const businessCity = schemaData.city;
        const businessState = schemaData.state;

        const seoData = generateCompleteSEO({
          title: businessName,
          description: schemaData.description || `${businessName} in ${businessCity}, ${businessState}. Contact us for ${real.services?.[0]?.title || 'services'}. ${schemaData.phone}`,
          keywords: [
            businessName,
            `${businessName} ${businessCity}`,
            real.businessType || 'business',
            ...(real.services?.slice(0, 5).map(s => s.title) || [])
          ],
          url: `/${slug}`,
          type: 'business.business',
          image: schemaData.logo
        });

        return (
          <div style={{ minHeight: '100vh', background: '#fff' }}>
            <SEOHead
              title={seoData.title}
              description={seoData.meta.description}
              keywords={seoData.meta.keywords}
              canonical={seoData.link.canonical}
              robots="index, follow"
              ogTitle={seoData.meta['og:title']}
              ogDescription={seoData.meta['og:description']}
              ogUrl={seoData.meta['og:url']}
              ogType="business.business"
              ogImage={schemaData.logo}
              twitterTitle={seoData.meta['twitter:title']}
              twitterDescription={seoData.meta['twitter:description']}
              twitterImage={schemaData.logo}
              geoPosition={schemaData.latitude ? `${schemaData.latitude};${schemaData.longitude}` : null}
          extraMeta={{
                'business:contact_data:phone_number': schemaData.phone,
                'business:contact_data:street_address': schemaData.streetAddress,
                'business:contact_data:locality': businessCity,
                'business:contact_data:region': businessState
              }}
            />

            <LocalBusinessSchema business={schemaData} />
            {renderTemplate(resolvedTemplateId || 1, real)}
          </div>
        );
      }
    } catch (e) {
      console.error('Failed to parse template data:', e);
    }
  }

  // Fallback: if website URL exists and is external, redirect to it
  const website = String(listing.website || '');
  if (website && website !== '' && !website.includes('/live-demo/')) {
    const url = /^https?:\/\//i.test(website) ? website : `https://${website}`;
    const currentUrl = typeof window !== 'undefined' ? window.location.href : '';
    if (url !== currentUrl) {
      window.location.replace(url);
      return <div style={{ padding: 24 }}>Redirecting...</div>;
    }
  }

  // No valid website data found
  return (
    <div style={{ padding: 24 }}>
      <h2>Website Not Available</h2>
      <p>This business listing does not have a website configured yet.</p>
    </div>
  );
}
